import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getAuthToken } from '@/utils/auth';
import type { Module } from '@/modules/user-settings/types';

export const usePermissionsStore = defineStore('permissions', () => {
  // State
  const userModules = ref<Module[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // Computed
  const allowedModuleIds = computed(() => 
    userModules.value.map(module => module.id)
  );

  const hasPermission = (moduleId: string): boolean => {
    // Check against actual allowed modules (from demo user or real user)
    return allowedModuleIds.value.includes(moduleId);
  };

  // Actions
  async function fetchUserPermissions(force = false) {
    if (isInitialized.value && !force) return; // Only fetch once per session unless forced
    
    loading.value = true;
    error.value = null;
    
    try {
      const token = await getAuthToken();
      
      // In development without auth, still fetch user-specific permissions
      if (!token && import.meta.env.DEV) {
        console.log('[PermissionsStore] Fetching demo user permissions...');
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${baseUrl}/api/permissions/user/modules`);
        const data = await response.json();
        console.log('[PermissionsStore] Received modules:', data.modules);
        userModules.value = data.modules;
        isInitialized.value = true;
        return;
      }
      
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/api/permissions/user/modules`, { headers });
      
      if (!response.ok) throw new Error('Failed to fetch user permissions');
      
      const data = await response.json();
      userModules.value = data.modules;
      isInitialized.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch permissions';
      console.error('Error fetching user permissions:', err);
      
      // In case of error, allow basic modules in development
      if (import.meta.env.DEV) {
        userModules.value = [
          { id: 'production-scheduler', name: 'Production Scheduler', description: '' },
          { id: 'production-manager', name: 'Production Manager', description: '' },
          { id: 'worker-skills', name: 'Worker Skills & Scheduling', description: '' }
        ];
      }
    } finally {
      loading.value = false;
    }
  }

  // Reset permissions (useful for logout)
  function reset() {
    userModules.value = [];
    isInitialized.value = false;
    error.value = null;
  }

  return {
    // State
    userModules,
    loading,
    error,
    
    // Computed
    allowedModuleIds,
    
    // Methods
    hasPermission,
    fetchUserPermissions,
    reset
  };
});